import type { JSX } from 'react';
import type { SortState } from '@/shared/types/table-filters.types';
import { CompanyUsersTable } from './CompanyUsersTable';
import { CompanyUserCard } from './CompanyUserCard';
import type { CompanyUser } from '../types/company-admin.types';

export interface CompanyUsersContentProps {
  users: CompanyUser[];
  isLoading: boolean;
  isError: boolean;
  /** Whether the query ever resolved. Distinguishes a cached empty list from a failed first load. */
  hasData: boolean;
  hasActiveFilters: boolean;
  sort: SortState | null;
  onToggleSort: (key: string) => void;
  currentUserId?: string | undefined;
  onEdit: (user: CompanyUser) => void;
}

function Message({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'destructive';
}): JSX.Element {
  const color = tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground';
  return <div className={`text-center py-8 ${color}`}>{children}</div>;
}

/** Desktop table and mobile cards render the same already filtered/sorted users. */
export function CompanyUsersContent({
  users,
  isLoading,
  isError,
  hasData,
  hasActiveFilters,
  ...listProps
}: CompanyUsersContentProps): JSX.Element {
  if (isLoading) return <Message>Loading users...</Message>;
  if (isError && !hasData) {
    return <Message tone="destructive">An error occurred while loading users</Message>;
  }
  if (users.length === 0) {
    return <Message>{hasActiveFilters ? 'No users match the filters' : 'No users found'}</Message>;
  }

  return (
    <>
      <div className="hidden md:block">
        <CompanyUsersTable users={users} {...listProps} />
      </div>
      <div className="grid gap-4 md:hidden">
        {users.map((user) => (
          <CompanyUserCard
            key={user.id}
            user={user}
            currentUserId={listProps.currentUserId}
            onEdit={listProps.onEdit}
          />
        ))}
      </div>
    </>
  );
}
